import { useEffect, useState } from 'react';
import { App, Form, Input, InputNumber, Modal, Radio, Select, Skeleton } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listProviders,
  createProvider,
  updateProvider,
  deleteProvider,
  testProvider,
  listGroups,
  createGroup,
  updateGroup,
  deleteGroup,
  setGroupDefault,
  type ModelProvider,
  type ModelProviderGroup,
  type CreateProviderBody,
  type PatchProviderBody,
  type CreateGroupBody,
  type ModelProviderType,
  type ReasoningEffort,
  REASONING_EFFORTS,
  DEFAULT_REASONING_EFFORT,
} from '../api/model-provider';
import { Topbar } from '../layouts/Topbar';
import { formatDateTime } from '../utils/datetime';
import { useAuthStore } from '../store/auth';
import { ModelEstimateEditor } from './usage/ModelEstimateEditor';

const AGENT_DISPLAY_NAMES: Record<string, string> = {
  'inquiry-agent': '询盘员工',
  'lead-extract-agent': '线索提取员工',
  'lead-routing-agent': '线索路由员工',
  'due-diligence-agent': '尽职调查员工',
  'conversation-summary-agent': '会话摘要员工',
  'translation-agent': '翻译员工',
};

const PROVIDER_TYPE_OPTIONS: { value: ModelProviderType; label: string }[] = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'azure-openai', label: 'Azure OpenAI' },
  { value: 'google', label: 'Google Gemini' },
  { value: 'xai', label: 'xAI Grok' },
  { value: 'deepseek', label: 'DeepSeek' },
  { value: 'qwen', label: '通义千问 / 百炼' },
  { value: 'kimi', label: 'Kimi / Moonshot' },
  { value: 'doubao', label: '豆包 / 火山方舟' },
  { value: 'anthropic', label: 'Anthropic（仅入库，当前不可执行）' },
];

const REASONING_EFFORT_OPTIONS = REASONING_EFFORTS.map((value) => ({ value, label: value }));

const AGENT_OPTIONS = [
  { value: 'inquiry-agent', label: AGENT_DISPLAY_NAMES['inquiry-agent'] },
  { value: 'lead-extract-agent', label: AGENT_DISPLAY_NAMES['lead-extract-agent'] },
  { value: 'lead-routing-agent', label: AGENT_DISPLAY_NAMES['lead-routing-agent'] },
  { value: 'due-diligence-agent', label: AGENT_DISPLAY_NAMES['due-diligence-agent'] },
  { value: 'conversation-summary-agent', label: AGENT_DISPLAY_NAMES['conversation-summary-agent'] },
  { value: 'translation-agent', label: AGENT_DISPLAY_NAMES['translation-agent'] },
];

function extractErrorMessage(e: unknown, fallback: string): string {
  return (
    (e as { response?: { data?: { message?: string } } })?.response?.data?.message
    ?? (e as Error)?.message
    ?? fallback
  );
}

type TabKey = 'provider' | 'group';

export function ModelProvidersPage() {
  const [tab, setTab] = useState<TabKey>('provider');

  return (
    <>
      <Topbar crumbs={[{ label: '系统' }, { label: '模型管理' }]} />
      <div className="page">
        <div className="page-header">
          <div className="page-header__title">
            <h1>模型管理</h1>
            <div className="page-header__sub">管理模型与编组，为数字员工分配模型资源</div>
          </div>
        </div>

        <div className="tab-bar">
          <span className={`tab${tab === 'provider' ? ' is-active' : ''}`} onClick={() => setTab('provider')}>模型</span>
          <span className={`tab${tab === 'group' ? ' is-active' : ''}`} onClick={() => setTab('group')}>编组</span>
        </div>

        {tab === 'provider' && <ProviderPanel />}
        {tab === 'group' && <GroupPanel />}
      </div>
    </>
  );
}

// ────────── Provider ──────────

function ProviderPanel() {
  const qc = useQueryClient();
  const { message, modal } = App.useApp();
  const isAdmin = useAuthStore((s) => s.isAdmin());
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ModelProvider | null>(null);
  const [testSession, setTestSession] = useState<{
    row: ModelProvider;
    reply?: string;
    error?: string;
  } | null>(null);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['model-providers'],
    queryFn: listProviders,
  });

  const rows = data ?? [];
  const invalidate = () => qc.invalidateQueries({ queryKey: ['model-providers'] });

  const removeMut = useMutation({
    mutationFn: deleteProvider,
    onSuccess: () => { message.success('已删除'); invalidate(); },
    onError: (e) => message.error(extractErrorMessage(e, '删除失败')),
  });

  const testMut = useMutation({
    mutationFn: (row: ModelProvider) => testProvider(row.id),
    onSuccess: (reply) => {
      setTestSession((s) => (s ? { ...s, reply, error: undefined } : s));
    },
    onError: (e) => {
      setTestSession((s) => (s ? { ...s, error: extractErrorMessage(e, '测试失败') } : s));
    },
  });

  function startTest(row: ModelProvider) {
    setTestSession({ row });
    testMut.mutate(row);
  }

  function confirmDelete(row: ModelProvider) {
    modal.confirm({
      title: '删除模型',
      content: `确定要删除「${row.model}」吗？若该模型仍被编组引用，删除会被拒绝。`,
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () => removeMut.mutateAsync(row.id),
    });
  }

  return (
    <>
      <div className="card">
        <div className="card__body" style={{ paddingBottom: 0 }}>
          <div className="toolbar">
            <div className="toolbar__right">
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => refetch()} disabled={isFetching}>
                <ReloadOutlined spin={isFetching} /> <span>刷新</span>
              </button>
              {isAdmin && (
                <button type="button" className="btn btn-primary btn-sm" onClick={() => setCreateOpen(true)}>
                  <PlusOutlined /> <span>新增模型</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {isLoading ? <div style={{ padding: 24 }}><Skeleton active /></div> : (
          <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
            <table className="table table--nowrap">
              <thead>
                <tr>
                  <th>模型</th>
                  <th>协议族</th>
                  <th>超时</th>
                  <th>推理档位</th>
                  <th>创建时间</th>
                  <th className="actions">操作</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="text-mono">{row.model}</td>
                    <td className="text-mono text-xs">{row.type}</td>
                    <td>{row.timeout_ms != null ? `${row.timeout_ms} ms` : <span className="text-muted">默认</span>}</td>
                    <td className="text-mono text-xs">{row.reasoning_effort}</td>
                    <td className="text-mono num text-xs" title={row.created_at}>{formatDateTime(row.created_at)}</td>
                    <td className="actions">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        disabled={testMut.isPending && testMut.variables?.id === row.id}
                        onClick={() => startTest(row)}
                      >
                        测试
                      </button>
                      {isAdmin && (
                        <>
                          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditTarget(row)}>编辑</button>
                          <button type="button" className="btn btn-ghost btn-sm" onClick={() => confirmDelete(row)}>删除</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', color: 'var(--c-fg-3)', padding: 24 }}>暂无数据</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isAdmin && [...new Set(rows.map((row) => row.model))].map((model) => (
        <ModelEstimateEditor key={model} model={model} />
      ))}

      <CreateProviderModal open={createOpen} onClose={() => setCreateOpen(false)} onDone={invalidate} />
      <EditProviderModal target={editTarget} onClose={() => setEditTarget(null)} onDone={invalidate} />
      <ProviderTestModal session={testSession} onClose={() => setTestSession(null)} />
    </>
  );
}

function ProviderTestModal({
  session,
  onClose,
}: {
  session: { row: ModelProvider; reply?: string; error?: string } | null;
  onClose: () => void;
}) {
  const row = session?.row;
  return (
    <Modal
      open={!!session}
      title="连通性测试"
      footer={null}
      onCancel={onClose}
      destroyOnClose
    >
      {row && (
        <div className="provider-test-log">
          <div>开始测试「{row.model}」</div>
          <div>协议族：{row.type}</div>
          <div>Base URL：{row.base_url || '默认'}</div>
          <div>发送测试消息：hello!</div>
          <div>等待响应…</div>
          {session?.reply != null && (
            <>
              <div className="provider-test-log__reply">{session.reply}</div>
              <div className="provider-test-log__ok">✓ 测试完成</div>
            </>
          )}
          {session?.error != null && (
            <>
              <div className="provider-test-log__err">{session.error}</div>
              <div className="provider-test-log__err">✗ 测试失败</div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

function CreateProviderModal({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone: () => void }) {
  const { message } = App.useApp();
  const [form] = Form.useForm<CreateProviderBody>();

  const mut = useMutation({
    mutationFn: createProvider,
    onSuccess: () => { message.success('创建成功'); form.resetFields(); onDone(); onClose(); },
    onError: (e) => message.error(extractErrorMessage(e, '创建失败')),
  });

  return (
    <Modal open={open} onCancel={onClose} onOk={() => form.submit()} title="新增模型" confirmLoading={mut.isPending} destroyOnClose>
      <Form
        form={form}
        layout="vertical"
        initialValues={{ type: 'openai', reasoningEffort: DEFAULT_REASONING_EFFORT }}
        onFinish={(v) => mut.mutate({
          type: v.type,
          model: v.model,
          apiKey: v.apiKey.trim(),
          baseUrl: v.baseUrl || null,
          timeoutMs: v.timeoutMs ?? null,
          reasoningEffort: v.reasoningEffort,
        })}
      >
        <Form.Item
          name="type"
          label="协议族"
          rules={[{ required: true, message: '请选择协议族' }]}
          extra="必须与真实厂商一致。直连方舟选豆包；中转选 OpenAI 且 Base URL 带版本前缀（如 /v1）。直连 DeepSeek / Gemini / Grok / 百炼 / Kimi 选对应项。"
        >
          <Select options={PROVIDER_TYPE_OPTIONS} />
        </Form.Item>
        <Form.Item name="model" label="模型" rules={[{ required: true, message: '请输入模型名' }]}>
          <Input autoComplete="off" />
        </Form.Item>
        <Form.Item name="apiKey" label="apiKey" rules={[{ required: true, message: '请输入 apiKey' }]}>
          <Input.Password autoComplete="new-password" />
        </Form.Item>
        <Form.Item name="baseUrl" label="Base URL"><Input autoComplete="off" placeholder="留空使用默认端点" /></Form.Item>
        <Form.Item name="timeoutMs" label="超时 (ms)">
          <InputNumber min={1} style={{ width: '100%' }} placeholder="留空使用默认超时" />
        </Form.Item>
        <Form.Item
          name="reasoningEffort"
          label="推理档位"
          rules={[{ required: true, message: '请选择推理档位' }]}
        >
          <Select options={REASONING_EFFORT_OPTIONS} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function EditProviderModal({ target, onClose, onDone }: { target: ModelProvider | null; onClose: () => void; onDone: () => void }) {
  const { message } = App.useApp();
  const [form] = Form.useForm();

  useEffect(() => {
    if (target) {
      form.setFieldsValue({
        type: target.type,
        model: target.model,
        apiKey: target.api_key_preview.trim(),
        baseUrl: target.base_url,
        timeoutMs: target.timeout_ms,
        reasoningEffort: target.reasoning_effort,
      });
    }
  }, [target, form]);

  const mut = useMutation({
    mutationFn: (body: PatchProviderBody) => updateProvider(target!.id, body),
    onSuccess: () => { message.success('已保存'); onDone(); onClose(); },
    onError: (e) => message.error(extractErrorMessage(e, '保存失败')),
  });

  function handleFinish(v: {
    type: ModelProviderType;
    model: string;
    apiKey?: string;
    baseUrl: string | null;
    timeoutMs: number | null;
    reasoningEffort: ReasoningEffort;
  }) {
    const body: PatchProviderBody = {
      type: v.type,
      baseUrl: v.baseUrl || null,
      model: v.model,
      timeoutMs: v.timeoutMs ?? null,
      reasoningEffort: v.reasoningEffort,
    };
    const preview = target?.api_key_preview.trim();
    const nextKey = v.apiKey?.trim();
    if (nextKey && nextKey !== preview) {
      body.apiKey = nextKey;
    }
    mut.mutate(body);
  }

  return (
    <Modal open={!!target} onCancel={onClose} onOk={() => form.submit()} title={`编辑模型 · ${target?.model ?? ''}`} confirmLoading={mut.isPending} destroyOnClose>
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item
          name="type"
          label="协议族"
          rules={[{ required: true, message: '请选择协议族' }]}
          extra="必须与真实厂商一致。直连方舟选豆包；中转选 OpenAI 且 Base URL 带版本前缀（如 /v1）。直连 DeepSeek / Gemini / Grok / 百炼 / Kimi 选对应项。"
        >
          <Select options={PROVIDER_TYPE_OPTIONS} />
        </Form.Item>
        <Form.Item name="model" label="模型" rules={[{ required: true, message: '请输入模型名' }]}>
          <Input autoComplete="off" />
        </Form.Item>
        <Form.Item name="apiKey" label="apiKey" extra="已保存密钥以掩码回显；不改则保持原值。">
          <Input autoComplete="off" />
        </Form.Item>
        <Form.Item name="baseUrl" label="Base URL"><Input autoComplete="off" placeholder="留空使用默认端点" /></Form.Item>
        <Form.Item name="timeoutMs" label="超时 (ms)">
          <InputNumber min={1} style={{ width: '100%' }} placeholder="留空使用默认超时" />
        </Form.Item>
        <Form.Item
          name="reasoningEffort"
          label="推理档位"
          rules={[{ required: true, message: '请选择推理档位' }]}
        >
          <Select options={REASONING_EFFORT_OPTIONS} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ────────── 编组 ──────────

function GroupPanel() {
  const qc = useQueryClient();
  const { message, modal } = App.useApp();
  const isAdmin = useAuthStore((s) => s.isAdmin());
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ModelProviderGroup | null>(null);

  const { data: groups, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['model-provider-groups'],
    queryFn: listGroups,
  });
  const { data: providers } = useQuery({
    queryKey: ['model-providers'],
    queryFn: listProviders,
  });

  const rows = groups ?? [];
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['model-provider-groups'] });
  };

  const removeMut = useMutation({
    mutationFn: deleteGroup,
    onSuccess: () => { message.success('已删除'); invalidate(); },
    onError: (e) => message.error(extractErrorMessage(e, '删除失败')),
  });

  const setDefaultMut = useMutation({
    mutationFn: setGroupDefault,
    onSuccess: () => { message.success('已设为全局默认，约 60 秒内对数字员工生效'); invalidate(); },
    onError: (e) => message.error(extractErrorMessage(e, '设为默认失败')),
  });

  function confirmDelete(row: ModelProviderGroup) {
    modal.confirm({
      title: '删除编组',
      content: `确定要删除「${row.name}」吗？若为全局默认编组，删除会被拒绝。`,
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () => removeMut.mutateAsync(row.id),
    });
  }

  function confirmSetDefault(row: ModelProviderGroup) {
    modal.confirm({
      title: '设为全局默认',
      content: `确定将「${row.name}」设为全局默认编组吗？`,
      okText: '确定',
      cancelText: '取消',
      onOk: () => setDefaultMut.mutateAsync(row.id),
    });
  }

  return (
    <>
      <div className="card">
        <div className="card__body" style={{ paddingBottom: 0 }}>
          <div className="toolbar">
            <div className="toolbar__right">
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => refetch()} disabled={isFetching}>
                <ReloadOutlined spin={isFetching} /> <span>刷新</span>
              </button>
              {isAdmin && (
                <button type="button" className="btn btn-primary btn-sm" onClick={() => setCreateOpen(true)}>
                  <PlusOutlined /> <span>新增编组</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {isLoading ? <div style={{ padding: 24 }}><Skeleton active /></div> : (
          <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>名称</th>
                  <th>是否全局默认</th>
                  <th>成员数</th>
                  <th>目标数字员工</th>
                  <th>超时</th>
                  <th className="actions">操作</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td><div className="text-strong">{row.name}</div></td>
                    <td>{row.is_global_default ? <span className="tag tag--role-admin">全局默认</span> : <span className="text-muted">—</span>}</td>
                    <td>{row.members.length}</td>
                    <td className="text-xs">{row.target_agents.map(key => AGENT_DISPLAY_NAMES[key] || key).join('、')}</td>
                    <td>{row.timeout_ms != null ? `${row.timeout_ms} ms` : <span className="text-muted">默认</span>}</td>
                    <td className="actions">
                      {isAdmin ? (
                        <>
                          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditTarget(row)}>编辑</button>
                          <button type="button" className="btn btn-ghost btn-sm" onClick={() => confirmDelete(row)}>删除</button>
                          {!row.is_global_default && (
                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => confirmSetDefault(row)}>设为默认</button>
                          )}
                        </>
                      ) : <span className="text-muted">—</span>}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', color: 'var(--c-fg-3)', padding: 24 }}>暂无数据</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <GroupFormModal
        mode="create"
        open={createOpen}
        providers={providers ?? []}
        target={null}
        onClose={() => setCreateOpen(false)}
        onDone={invalidate}
      />
      <GroupFormModal
        mode="edit"
        open={!!editTarget}
        providers={providers ?? []}
        target={editTarget}
        onClose={() => setEditTarget(null)}
        onDone={invalidate}
      />
    </>
  );
}

interface MemberRow {
  providerId: number;
  priority: number;
}

function GroupFormModal({
  mode, open, target, providers, onClose, onDone,
}: {
  mode: 'create' | 'edit';
  open: boolean;
  target: ModelProviderGroup | null;
  providers: ModelProvider[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [memberRows, setMemberRows] = useState<MemberRow[]>([]);
  const [defaultProviderId, setDefaultProviderId] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && target) {
      form.setFieldsValue({
        name: target.name,
        targetAgents: target.target_agents,
        timeoutMs: target.timeout_ms,
        memberIds: target.members.map((m) => m.providerId),
      });
      setMemberRows(target.members.map((m) => ({ providerId: m.providerId, priority: m.priority })));
      setDefaultProviderId(target.members.find((m) => m.isDefault)?.providerId ?? null);
    } else {
      form.resetFields();
      setMemberRows([]);
      setDefaultProviderId(null);
    }
  }, [open, mode, target, form]);

  function handleMemberIdsChange(ids: number[]) {
    setMemberRows((prev) => {
      const kept = prev.filter((r) => ids.includes(r.providerId));
      const added = ids
        .filter((id) => !prev.some((r) => r.providerId === id))
        .map((id) => ({ providerId: id, priority: 100 }));
      return [...kept, ...added];
    });
    setDefaultProviderId((prev) => (prev != null && ids.includes(prev) ? prev : null));
  }

  function updatePriority(providerId: number, priority: number | null) {
    setMemberRows((prev) => prev.map((r) => (r.providerId === providerId ? { ...r, priority: priority ?? 0 } : r)));
  }

  const createMut = useMutation({
    mutationFn: createGroup,
    onSuccess: () => { message.success('创建成功'); onDone(); onClose(); },
    onError: (e) => message.error(extractErrorMessage(e, '创建失败')),
  });
  const updateMut = useMutation({
    mutationFn: (body: CreateGroupBody) => updateGroup(target!.id, body),
    onSuccess: () => { message.success('已保存'); onDone(); onClose(); },
    onError: (e) => message.error(extractErrorMessage(e, '保存失败')),
  });
  const mut = mode === 'create' ? createMut : updateMut;

  function handleFinish(v: { name: string; targetAgents: string[]; timeoutMs: number | null; memberIds: number[] }) {
    if (!v.memberIds || v.memberIds.length === 0) {
      message.error('请至少选择一个成员 provider');
      return;
    }
    if (defaultProviderId == null) {
      message.error('请指定一个默认成员');
      return;
    }
    const members = memberRows.map((r) => ({
      providerId: r.providerId,
      priority: r.priority,
      isDefault: r.providerId === defaultProviderId,
    }));
    mut.mutate({
      name: v.name,
      targetAgents: v.targetAgents,
      timeoutMs: v.timeoutMs ?? null,
      members,
    });
  }

  const providerOptions = providers.map((p) => ({ value: p.id, label: p.model }));
  const providerById = new Map(providers.map((p) => [p.id, p]));

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      title={mode === 'create' ? '新增编组' : `编辑编组 · ${target?.name ?? ''}`}
      confirmLoading={mut.isPending}
      destroyOnClose
      width={640}
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
          <Input autoComplete="off" />
        </Form.Item>
        <Form.Item name="targetAgents" label="目标数字员工" rules={[{ required: true, message: '请至少选择一个数字员工' }]}>
          <Select mode="multiple" options={AGENT_OPTIONS} placeholder="选择目标数字员工" />
        </Form.Item>
        <Form.Item name="timeoutMs" label="超时 (ms)">
          <InputNumber min={1} style={{ width: '100%' }} placeholder="留空使用默认超时" />
        </Form.Item>
        <Form.Item name="memberIds" label="成员模型" rules={[{ required: true, message: '请至少选择一个成员' }]}>
          <Select mode="multiple" options={providerOptions} placeholder="选择成员模型" onChange={handleMemberIdsChange} />
        </Form.Item>
      </Form>

      {memberRows.length > 0 && (
        <div className="field" style={{ marginTop: 8 }}>
          <div className="field__label">成员优先级 / 默认成员</div>
          <Radio.Group
            value={defaultProviderId}
            onChange={(e) => setDefaultProviderId(e.target.value)}
            style={{ width: '100%' }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {memberRows.map((row) => {
                const p = providerById.get(row.providerId);
                return (
                  <div key={row.providerId} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Radio value={row.providerId} style={{ minWidth: 220 }}>
                      {p ? p.model : `#${row.providerId}`}
                    </Radio>
                    <span className="text-xs text-muted">优先级</span>
                    <InputNumber
                      min={0}
                      value={row.priority}
                      onChange={(v) => updatePriority(row.providerId, v)}
                      style={{ width: 100 }}
                    />
                  </div>
                );
              })}
            </div>
          </Radio.Group>
          <div className="field__hint">恰好一个成员需被选为默认（isDefault）。</div>
        </div>
      )}
    </Modal>
  );
}
